import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import ToastBody from "@/components/ui/toast-body";
import CreateLeadModal from "@/pages/leads/components/CreateLeadModal";
import CreateDealModal from "@/pages/deals/components/CreateDealModal";
import CreateContractModal from "@/pages/contracts/components/CreateContractModal";
import {
  EntityType,
  useEntityType
} from "@/services/hooks/entities/useEntityType";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { UserData } from "@/entities/users/types";
import { useResetFilteredDeals } from "@/pages/deals/hooks";
import { useResetLeadsInfinite } from "@/pages/leads/hooks";

export const usePartnerContactActions = (
  partnerContactEntity: PartnerContactData | undefined,
  userData?: UserData,
  isTabView?: boolean,
  forceRefetch?: boolean,
  includeAdditionals?: boolean
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleCopyLink = useCopyLink();

  const {
    partnerContact,
    resetPartnerContacts,
    onUpdatePartnerContactField: onUpdateField,
    deletePartnerContact
  } = useEntityType(
    EntityType.PARTNER_CONTACT,
    partnerContactEntity,
    false,
    undefined,
    undefined,
    forceRefetch,
    includeAdditionals
  );

  const resetFilteredDeals = useResetFilteredDeals();
  const resetLeadsInfinite = useResetLeadsInfinite();

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false);
  const [isCreateContractModalOpen, setIsCreateContractModalOpen] =
    useState(false);

  const toggleTasksDrawer = () => setIsTasksDrawerOpen(!isTasksDrawerOpen);

  const handleDelete = (
    setIsOpen: Dispatch<SetStateAction<boolean>>,
    isQuickView: boolean = false,
    closeDrawer?: () => void
  ) => {
    if (partnerContact) {
      deletePartnerContact.mutate(
        {
          pathParams: { partnerContactId: partnerContact.id }
        },
        {
          onSuccess: () => {
            if (isQuickView) {
              setIsOpen(false);
              closeDrawer?.();
            } else {
              navigate("/people", { replace: true });
            }
            resetPartnerContacts();
            toast.success(
              <ToastBody
                title={`${t("partners.partnerContactView.toast1")} ${partnerContact ? getPartnerContactFullName(partnerContact) : ""} ${t("partners.partnerContactView.toast2")}`}
              />,
              {
                duration: 5000,
                icon: null
              }
            );
          }
        }
      );
    }
  };

  const onCreateLead = userData
    ? () => setIsCreateLeadModalOpen(true)
    : undefined;
  const onCreateDeal = userData
    ? () => setIsCreateDealModalOpen(true)
    : undefined;
  const onCreateContract = userData
    ? () => setIsCreateContractModalOpen(true)
    : undefined;

  const getCreateOptions = () => {
    if (!userData) return {};
    return {
      lead: onCreateLead,
      deal: onCreateDeal,
      ...(onCreateContract && { contract: onCreateContract })
    } as DetailedViewOptions;
  };

  const handleCreateSuccess = () => {
    resetFilteredDeals();
    resetLeadsInfinite();
  };

  const getOptions = (isQuickView: boolean = false, partnerContactId?: string) => {
    return {
      copyLink: () =>
        handleCopyLink(
          isQuickView,
          partnerContactId,
          isTabView ? "/people" : undefined
        ),
      partnerContactLog: () =>
        isQuickView
          ? navigate(`/partners_contacts/${partnerContactId}/log`, {
              replace: true
            })
          : navigate("log"),
      delete: {
        action: () => setIsOpenDeleteModal(true),
        variant: "destructive"
      }
    } as DetailedViewOptions;
  };

  const renderModals = (
    isDropdownClosing = false,
    isQuickView = false,
    onClose?: () => void,
    userData?: UserData
  ) => (
    <>
      <DeleteEntityModal
        isOpen={isOpenDeleteModal && !isDropdownClosing}
        setIsOpen={setIsOpenDeleteModal}
        entity={EntityType.PARTNER_CONTACT}
        entityName={
          partnerContact ? getPartnerContactFullName(partnerContact) : ""
        }
        handleDelete={() =>
          handleDelete(setIsOpenDeleteModal, isQuickView, onClose)
        }
      />
      {userData && (
        <>
          <CreateLeadModal
            isOpen={isCreateLeadModalOpen && !isDropdownClosing}
            setIsOpen={setIsCreateLeadModalOpen}
            userData={userData}
            resetPage={handleCreateSuccess}
          />
          <CreateDealModal
            isOpen={isCreateDealModalOpen && !isDropdownClosing}
            setIsOpen={setIsCreateDealModalOpen}
            userData={userData}
            onDealCreated={handleCreateSuccess}
          />
          <CreateContractModal
            isOpen={isCreateContractModalOpen && !isDropdownClosing}
            setIsOpen={setIsCreateContractModalOpen}
            userData={userData}
            onContractCreated={handleCreateSuccess}
          />
        </>
      )}
    </>
  );

  return {
    partnerContact,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getCreateOptions,
    getOptions,
    renderModals,
    onCreateLead,
    onCreateDeal,
    onCreateContract
  };
};
